import { logger } from 'services/Logger';
import { Play } from '../../spinnaxBase/logic/types_storage';
import { getAuth } from '../../util';
import { CLASSIFIER_URL } from 'util/constants';

export interface ClassificationInfo {
  service: string;
  version: string;
  confidence_threshold: number;
  figures_enhanced: number;
  total_figures: number;
}

export interface ClassificationResponse {
  status: string;
  play: Play;
  classification_info: ClassificationInfo;
  user_id: string;
}

export interface ClassifierInfo {
  status: string;
  service: string;
  version: string;
  available_classes: string[];
  sequence_length: number;
  confidence_threshold: number;
  model_parameters: any;
  model_loaded: boolean;
  user_id: string;
}

export class FreakClassifierService {
  private isInitialized = false;

  constructor() {
    this.runInitTest();
  }

  /**
   * Enhance Play object with figure classification
   * @param play - The play data to enhance
   * @returns Promise with enhanced play data
   */
  async enhancePlayWithClassification(play: Play): Promise<Play> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        return play; // Return original play without classification
      }
      const token = await user.getIdToken();
      const requestPayload = JSON.stringify(play);
      const response = await fetch(`${CLASSIFIER_URL}/classify`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: requestPayload,
      });

      if (!response.ok) {
        const errorText = await response.text();
        return play; // Return original play on API failure
      }

      const result: ClassificationResponse = await response.json();
      return result.play;
    } catch (error) {
      logger.warn(
        '[FreakClassifier] Classification error - using original play:',
        {
          errorType: error?.constructor?.name,
          errorMessage: error instanceof Error ? error.message : String(error),
          willUseOriginalPlay: true,
        }
      );
      return play;
    }
  }

  /**
   * Check if the classifier service is healthy
   * @returns Promise<boolean> - true if service is healthy
   */
  async isHealthy(): Promise<boolean> {
    try {
      const response = await fetch(`${CLASSIFIER_URL}/health`, {
        method: 'GET',
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get classifier information
   * @returns Promise with classifier info
   */
  async getClassifierInfo(): Promise<ClassifierInfo | null> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        return null;
      }

      const token = await user.getIdToken();

      const response = await fetch(`${CLASSIFIER_URL}/info`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      return result;
    } catch (error) {
      return null;
    }
  }

  /**
   * Initialize and test the classifier service
   */
  private async runInitTest(): Promise<void> {
    try {
      // Health Check
      const isHealthy = await this.isHealthy();

      if (!isHealthy) {
        this.isInitialized = false;
        return;
      }

      // Auth Check
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        this.isInitialized = false;
        return;
      }

      this.isInitialized = true;
    } catch (error) {
      logger.error('❌ FreakClassifier Service init failed:', error);
      this.isInitialized = false;
    }
  }

  // Getter for initialization status
  get initialized(): boolean {
    return this.isInitialized;
  }

  // Method to explicitly trigger initialization (e.g., after login)
  public async ensureInitialized(): Promise<void> {
    if (!this.isInitialized) {
      await this.runInitTest();
    }
  }
}

// Singleton export
export const freakClassifierService = new FreakClassifierService();
